// src/pages/TrackCreation/Step1.tsx
import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RouteParamsWithLanguage } from "../../../../../../types/screensTypes";
import ImageList from "../../../../../../components/images/ImageList";
import Button from "../../../../../../components/ui/Button";
import { saveSubPlan, getSubplanbyId } from "../../../../../../api/museum";
import { FileInput } from "../../../../../../components/common/FileInput";
import TitleSubtitle from "../../../../../../components/common/TitleSubtitle";
import { useTrackCreation } from "../../../../../../context/TrackCreationContext";
import { uploadFileToS3Complete } from "../../../../../../api/s3";
import { IMAGE_FORMAT, IMAGE_MAX_SIZE_4_MB } from "../../../../../../utils/image";
import ImageSpecsBadges from "../../../../../../components/common/ImageSpecsBadges";
import { useImageValidation } from "../../../../../../hooks/useImageValidation";
import floorEdit from "./FloorEdit.module.css";
import stepStyles from "../../../createStep.module.css";

const RoomPlan: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { trackData } = useTrackCreation();
    const { roomId, language } = useParams<RouteParamsWithLanguage>();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [floorNumber, setFloorNumber] = useState<number | null>(null);
    const [planUrl, setPlanUrl] = useState<string | null>(null);
    const imageValidation = useImageValidation({
        type: "floorPlan",
        imageUrl: planUrl || undefined,
    });

    const uploadTitle = trackData && trackData.isMuseumList ? t("floorImageOptional") : t("uploadPlan");
    const uploadSubtitle =
        trackData && trackData.isMuseumList
            ? t("pleaseInsertImageForVisitorsToSee")
            : t("pleaseInsertMapForVisitorsToSee");

    const handleFileSelected = async (files: File | File[]) => {
        const file = Array.isArray(files) ? files[0] : files; // Toujours prendre un seul fichier

        if (file) {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("No authentication token found");
                return;
            }

            await imageValidation.validateImage(file);

            uploadFileToS3Complete(
                {
                    file,
                    isPublic: true, // Public files for general uploads
                    isFolder: false, // No folder structure for general uploads
                },
                token,
            )
                .then((result: string) => {
                    setPlanUrl(result);
                })
                .catch((error) => {
                    console.log(error);
                });
        }
    };

    const saveRoom = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId || !roomId || !language) return;
        await saveSubPlan({
            token,
            trackId,
            subplanId: roomId,
            language,
            planUrl: planUrl ? planUrl : undefined,
            floorNumber: floorNumber ? floorNumber : 0,
            roomNumber: 0,
            planByLanguageUrl: planUrl ? planUrl : undefined,
        });
        // Replace the current path (matching /room-[^/]+/...) with '/museum-architecture'
        const newPath = location.pathname.replace(/\/room-[^/]+\/.+$/, "/museum-architecture");
        navigate(newPath);
    };

    const getRoomInfo = async (trackId: string, roomId: string, language: string): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token || !trackId || !roomId || !language) return;
        const subplanInfo = await getSubplanbyId({
            token,
            trackId,
            subplanId: roomId,
            language,
        });
        const floorNum = subplanInfo.floorNumber;
        setFloorNumber(floorNum);
        setPlanUrl(subplanInfo.planUrl);
    };

    useEffect(() => {
        if (roomId && roomId !== "" && roomId !== "null" && trackData.trackId && language) {
            getRoomInfo(trackData.trackId, roomId, language);
        }
    }, [roomId, trackData.trackId, language]);

    const setPicturesFromList = (pictures: string[]): void => {
        if (pictures && pictures.length > 0) {
            setPlanUrl(pictures[0]);
        } else {
            setPlanUrl(null);
        }
    };

    return (
        <div className={`experience-floor-edit ${floorEdit.root}`}>
            <TitleSubtitle
                title={uploadTitle}
                subtitle={uploadSubtitle}
                addMargin={false}
                classNameTitle={floorEdit.pageTitle}
                classNameSubtitle={floorEdit.pageSub}
            />

            <h3 className={`experience-floor-h experience-floor-h--gap ${floorEdit.reqHeading}`}>
                {t("requirements")}
            </h3>
            <ImageSpecsBadges spec="floorPlan" warnings={imageValidation.warnings} />
            <p className={`experience-floor-req ${floorEdit.req}`}>
                ✕ {t("noPhotographerWatermarksLogosOrReadableLicensePlates")}
            </p>

            <div className={stepStyles.fileUpload}>
                <FileInput
                    onFileSelected={handleFileSelected}
                    showButton={true}
                    buttonText={t("add")}
                    placeholder={t("dragAndDropOrClick")}
                    acceptedFileTypes={IMAGE_FORMAT}
                    maxSize={IMAGE_MAX_SIZE_4_MB}
                    maxSizeLabel={t("imageMaxSize4Mb")}
                />
            </div>
            <div className="photo-gallery">
                <ImageList
                    pictures={planUrl ? [planUrl] : []}
                    setPictures={(pictures: string[]) => setPicturesFromList(pictures)}
                />
            </div>
            <div className={`${stepStyles.stepButton} ${floorEdit.actions}`}>
                <Button
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                    label={t("continue")}
                    onClick={saveRoom}
                />
            </div>
        </div>
    );
};

export default RoomPlan;
